import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { MasspUseCaseValidatorUiOptions } from './schema';
import { useMasspUseCaseValidator } from './MasspUseCaseValidator.logic';
import {
  Colors,
  renderUseCaseInfoValue,
  useIsDarkMode,
  useAgentGardenWebResources,
} from '@mercedes-benz/backstage-plugin-aiecosystem';

export { MasspUseCaseValidatorSchema } from './schema';

/**
 * A scaffolder field for entering and validating a MASSP4AI use case ID.
 * The Validate button checks the ID against the Agent Garden API and, on
 * success, displays the fetched MASSP4AI use case information below the input.
 *
 * @param props - Field extension component props
 * @returns The MASSP4AI use case ID validator field
 */
export const MasspUseCaseValidator = (
  props: FieldExtensionComponentProps<string, MasspUseCaseValidatorUiOptions>,
) => {
  const {
    description,
    getHelperText,
    getHelperTextColor,
    handleInputChange,
    handleValidateClick,
    isInvalid,
    isValid,
    isFoundButInvalid,
    isEditMode,
    isUseCaseIdFromFormData,
    isValidating,
    required,
    title,
    useCaseId,
    useCaseInfo,
  } = useMasspUseCaseValidator(props);

  const cardBackgroundColor = useIsDarkMode()
    ? Colors.Themes.Dark.backgroundDefault
    : Colors.Themes.Light.backgroundDefault;

  const agentGardenWebResources = useAgentGardenWebResources();

  /**
   * Renders the end adornment for the input field based on the current validation state.
   * @returns The end adornment component or undefined
   */
  const renderEndAdornment = () => {
    if (isValidating) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} />
        </InputAdornment>
      );
    }

    if (isValid) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon color="success" />
        </InputAdornment>
      );
    }

    if (isInvalid) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon color="error" />
        </InputAdornment>
      );
    }

    return undefined;
  };

  /**
   * Determines the color of the text field based on the current validation state.
   * @returns The color of the text field
   */
  const textFieldColor = () => {
    if (isInvalid || isFoundButInvalid) {
      return 'error';
    }
    if (isValid) {
      return 'success';
    }
    return 'primary';
  };

  /**
   * Renders the helper text for the current validation state. The default
   * description is enriched with a link to the MASSP4AI use case
   * registration page; all validation status messages are rendered as-is.
   * @returns The helper text content
   */
  const renderHelperText = () => {
    const helperText = getHelperText();

    if (helperText !== description) {
      return helperText;
    }

    return (
      <>
        {helperText} If you have not already registered a use case, you can do
        so{' '}
        <Link
          href={agentGardenWebResources.MASSP4AIUseCaseRegistration.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          here
          <OpenInNewIcon fontSize="inherit" sx={{ marginLeft: 0.5 }} />
        </Link>
      </>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <TextField
          fullWidth
          label={title}
          required={required}
          value={useCaseId}
          onChange={handleInputChange}
          variant="standard"
          error={isInvalid || isFoundButInvalid}
          color={textFieldColor()}
          focused={isValid}
          helperText={renderHelperText()}
          FormHelperTextProps={{ sx: { color: getHelperTextColor() } }}
          InputProps={{
            endAdornment: renderEndAdornment(),
          }}
        />

        <Button
          variant="outlined"
          onClick={handleValidateClick}
          disabled={isValidating}
          sx={{ mt: 1, whiteSpace: 'nowrap' }}
        >
          Validate
        </Button>
      </Box>

      {(isValid || isFoundButInvalid) && useCaseInfo && (
        <Box sx={{ mt: '1rem' }}>
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              padding: 2,
              maxWidth: '100%',
              background: cardBackgroundColor,
            }}
          >
            <Typography
              variant="h4"
              sx={{ mb: 2, color: 'text.primary', size: '26px' }}
            >
              MASSP4AI Use Case Information
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, color: 'text.secondary', size: '16px' }}
            >
              The following information was pulled from your Use Case ID.
            </Typography>

            {isEditMode && isValid && isUseCaseIdFromFormData && (
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                To manage the following information, you will need to update on
                MASSP4AI directly.
              </Alert>
            )}

            <Table
              size="small"
              aria-label="MASSP4AI use case details"
              sx={{ width: '100%' }}
            >
              <TableBody>
                {Object.entries(useCaseInfo ?? {}).map(([key, entry]) => (
                  <TableRow
                    key={key}
                    sx={{
                      '& td': { borderBottomColor: 'divider', py: 2 },
                      '&:last-child td': { borderBottom: 'none' },
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <TableCell
                      sx={{
                        width: '30%',
                        fontWeight: 'bold',
                        verticalAlign: 'top',
                      }}
                    >
                      {entry.label}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'text.secondary',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {renderUseCaseInfoValue(key, entry.value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Box>
      )}
    </Box>
  );
};
